import { useEffect, useMemo, useState } from "react";
import { PauseCircle, SlidersHorizontal, X, Trash2, PlusCircle, CalendarClock, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, inr, currentMonth, monthLabel, formatApiError, FEE_LABELS } from "../../lib/api";
import { MonthNav } from "../../components/MonthNav";
import { useTaxonomy } from "../../lib/useTaxonomy";
import { packageByKey, fmtIso } from "../../lib/packages";
import { AddDuesModal } from "../../components/AddDuesModal";
import { PackageChangeModal } from "../../components/PackageChangeModal";

const inputCls = "bg-[#0F0F0F] border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#D4AF37]";

const OverrideModal = ({ row, month, onClose, onSaved }) => {
  const [charges, setCharges] = useState({ group: "", transport: "", pt: "", previous_dues: "" });
  const [reason, setReason] = useState(row.override?.reason || "");

  const submit = async (e) => {
    e.preventDefault();
    const clean = {};
    for (const t of Object.keys(charges)) if (charges[t] !== "") clean[t] = Number(charges[t]);
    if (Object.keys(clean).length === 0) { toast.error("Enter at least one amount"); return; }
    try {
      await api.post("/admin/overrides", { player_id: row.player.player_id, month, charges: clean, reason });
      toast.success("Month adjusted");
      onSaved();
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/overrides/${row.override.id}`);
      toast.success("Override removed");
      onSaved();
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4" onClick={onClose} data-testid="override-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-[#0A0A0A] border border-white/10 p-8 w-full max-w-md">
        <div className="flex justify-between items-start mb-2">
          <h2 className="font-heading text-xl font-black">Adjust This Month</h2>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>
        <p className="text-white/50 text-sm mb-6">{row.player.name} · {monthLabel(month)} only — the regular plan resumes next month.</p>
        {row.override && (
          <div className="flex items-center justify-between border border-[#D4AF37]/40 bg-[#D4AF37]/10 p-3 mb-5 text-xs" data-testid="override-existing">
            <span className="text-[#D4AF37]">Existing override: {Object.entries(row.override.charges).map(([t, v]) => `${FEE_LABELS[t]} ${inr(v)}`).join(", ")}</span>
            <button type="button" onClick={remove} className="text-red-400 hover:text-red-300" data-testid="override-remove"><Trash2 size={15} /></button>
          </div>
        )}
        <p className="text-white/40 text-xs mb-3">Plan charges: {["group", "transport", "pt", "previous_dues"].map((t) => `${FEE_LABELS[t]} ${inr(row.charges[t])}`).join(" · ")}</p>
        <div className="grid grid-cols-2 gap-3 mb-4">
          {["group", "transport", "pt", "previous_dues"].map((t) => (
            <input key={t} type="number" min="0" placeholder={`${FEE_LABELS[t]} ₹`} value={charges[t]}
              onChange={(e) => setCharges({ ...charges, [t]: e.target.value })} className={inputCls} data-testid={`ov-${t}`} />
          ))}
        </div>
        <input placeholder="Reason (e.g. injury discount)" value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls + " w-full mb-4"} data-testid="ov-reason" />
        <button type="submit" className="w-full bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-xs px-6 py-3 rounded-sm hover:bg-[#F3D059] transition-colors duration-200" data-testid="ov-save">Save Adjustment</button>
      </form>
    </div>
  );
};

export default function Dues() {
  const [month, setMonth] = useState(currentMonth());
  const [rows, setRows] = useState([]);
  const [pauseFor, setPauseFor] = useState(null);
  const [reason, setReason] = useState("");
  const [adjustRow, setAdjustRow] = useState(null);
  const [addDuesFor, setAddDuesFor] = useState(null);
  const [pkgRow, setPkgRow] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [feeTypeFilter, setFeeTypeFilter] = useState("all");
  const [centreFilter, setCentreFilter] = useState("");
  const [batchFilter, setBatchFilter] = useState("");
  const [search, setSearch] = useState("");
  const { centreNames, batchNames } = useTaxonomy();

  const load = () => api.get("/admin/dues", { params: { month } }).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [month]);

  const addPause = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/pauses", { player_id: pauseFor, month, reason });
      toast.success("Billing paused for this month");
      setPauseFor(null);
      setReason("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeExtra = async (x) => {
    if (!window.confirm(`Remove "${x.label}" (${inr(x.amount)})?`)) return;
    try { await api.delete(`/admin/extra-charges/${x.id}`); toast.success("Charge removed"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const perTypeCumulative = (r, type) =>
    (r._by_month || []).reduce((s, m) => s + (m.outstanding?.[type] || 0), 0);

  const filtered = useMemo(() => rows.filter((r) => {
    if (centreFilter && r.player.centre !== centreFilter) return false;
    if (batchFilter && r.player.batch !== batchFilter) return false;
    if (statusFilter === "paid" && r.cumulative > 0) return false;
    if (statusFilter === "pending" && r.cumulative <= 0) return false;
    if (feeTypeFilter !== "all" && perTypeCumulative(r, feeTypeFilter) <= 0) return false;
    if (search && !`${r.player.player_id} ${r.player.name}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [rows, statusFilter, feeTypeFilter, centreFilter, batchFilter, search]);

  const totals = useMemo(() => filtered.reduce((acc, r) => ({
    billed: acc.billed + r.total_charges,
    paid: acc.paid + r.total_paid,
    outstanding: acc.outstanding + r.cumulative,
  }), { billed: 0, paid: 0, outstanding: 0 }), [filtered]);

  return (
    <div data-testid="admin-dues">
      <div className="flex flex-wrap items-center justify-between gap-6 mb-6">
        <MonthNav month={month} onChange={setMonth} testid="dues-month-nav" />
        <div className="flex items-center gap-4">
          <p className="text-white/40 text-xs uppercase tracking-widest" data-testid="dues-count">{filtered.length} of {rows.length} shown</p>
          <button onClick={() => setAddDuesFor([])} className="flex items-center gap-2 bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-widest px-4 py-2.5 rounded-sm hover:bg-[#F3D059] transition-colors duration-200" data-testid="add-dues-bulk-btn">
            <Plus size={14} /> Add Dues
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-4" data-testid="dues-filters">
        <input placeholder="Search name / ID" value={search} onChange={(e) => setSearch(e.target.value)} className={inputCls + " w-56"} data-testid="dues-search" />
        <div className="flex border border-white/10 rounded-sm overflow-hidden">
          {[["all", "All"], ["pending", "Pending"], ["paid", "Paid"]].map(([v, label]) => (
            <button key={v} onClick={() => setStatusFilter(v)}
              className={`text-xs uppercase tracking-widest px-4 py-2.5 transition-colors duration-200 ${
                statusFilter === v ? "bg-[#D4AF37] text-black" : "text-white/60 hover:text-white"
              }`}
              data-testid={`dues-status-${v}`}>
              {label}
            </button>
          ))}
        </div>
        <select value={centreFilter} onChange={(e) => setCentreFilter(e.target.value)} className={inputCls + " w-auto"} data-testid="dues-filter-centre">
          <option value="">All Centres</option>
          {centreNames.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={batchFilter} onChange={(e) => setBatchFilter(e.target.value)} className={inputCls + " w-auto"} data-testid="dues-filter-batch">
          <option value="">All Batches</option>
          {batchNames.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        {(statusFilter !== "all" || feeTypeFilter !== "all" || centreFilter || batchFilter || search) && (
          <button onClick={() => { setStatusFilter("all"); setFeeTypeFilter("all"); setCentreFilter(""); setBatchFilter(""); setSearch(""); }}
            className="text-[10px] uppercase tracking-widest text-white/40 hover:text-[#D4AF37] px-3"
            data-testid="dues-clear-filters">Clear filters</button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="dues-fee-type-filters">
        <span className="text-[10px] uppercase tracking-[0.24em] text-white/40 mr-1">Fee type</span>
        {[
          ["all", "All"],
          ["group", "Group Sessions"],
          ["transport", "Transport"],
          ["pt", "Personal Training"],
          ["registration", "Registration"],
          ["previous_dues", "Previous Dues"],
          ["extra", "Additional Charges"],
        ].map(([v, label]) => (
          <button key={v} onClick={() => setFeeTypeFilter(v)}
            className={`text-[10px] uppercase tracking-widest px-3 py-1.5 border transition-colors duration-200 ${
              feeTypeFilter === v
                ? "bg-[#D4AF37] border-[#D4AF37] text-black"
                : "border-white/15 text-white/60 hover:border-[#D4AF37]/50 hover:text-white"
            }`}
            data-testid={`dues-fee-type-${v}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10 mb-6" data-testid="dues-summary">
        <div className="bg-[#0A0A0A] p-4">
          <p className="text-[10px] uppercase tracking-widest text-white/40">Billed</p>
          <p className="font-heading text-xl font-black mt-1">{inr(totals.billed)}</p>
        </div>
        <div className="bg-[#0A0A0A] p-4">
          <p className="text-[10px] uppercase tracking-widest text-white/40">Collected</p>
          <p className="font-heading text-xl font-black mt-1">{inr(totals.paid)}</p>
        </div>
        <div className="bg-[#0A0A0A] p-4">
          <p className="text-[10px] uppercase tracking-widest text-white/40">Outstanding</p>
          <p className={`font-heading text-xl font-black mt-1 ${totals.outstanding > 0 ? "text-[#D4AF37]" : "text-white"}`}>{inr(totals.outstanding)}</p>
        </div>
      </div>

      <div className="space-y-2">
        {filtered.map((r) => (
          <div key={r.player.player_id} className="border border-white/10 bg-[#0A0A0A] p-4 sm:p-5" data-testid={`dues-row-${r.player.player_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-sm">{r.player.name} <span className="text-[#D4AF37] text-xs ml-1">{r.player.player_id}</span>
                  {r.package && (
                    <span className={`ml-2 text-[10px] uppercase tracking-widest px-2 py-0.5 border ${r.package.due_date ? "border-[#D4AF37]/60 text-[#D4AF37]" : "border-white/15 text-white/50"}`} data-testid={`dues-package-${r.player.player_id}`}>
                      {packageByKey(r.package.package)?.label} · {r.package.due_date ? `${r.package.is_start ? "starts" : "renewal due"} ${fmtIso(r.package.due_date)}` : `renews ${fmtIso(r.package.next_date)}`}
                    </span>
                  )}
                </p>
                <p className="text-white/40 text-xs mt-0.5">{r.player.batch || "No batch"} · {r.player.centre || "No centre"}</p>
              </div>
              <div className="flex items-center gap-4 sm:gap-8 text-sm">
                <div className="text-right"><p className="text-white/40 text-[10px] uppercase tracking-widest">Billed</p><p className="font-semibold">{inr(r.total_charges)}</p></div>
                <div className="text-right"><p className="text-white/40 text-[10px] uppercase tracking-widest">Paid</p><p className="font-semibold">{inr(r.total_paid)}</p></div>
                <div className="text-right"><p className="text-white/40 text-[10px] uppercase tracking-widest">Month Due</p><p className={`font-bold ${r.total_outstanding > 0 ? "text-[#D4AF37]" : "text-white/40"}`}>{inr(r.total_outstanding)}</p></div>
                <div className="text-right"><p className="text-white/40 text-[10px] uppercase tracking-widest">Total Due</p><p className={`font-bold ${r.cumulative > 0 ? "text-[#D4AF37]" : "text-green-400"}`} data-testid={`dues-cumulative-${r.player.player_id}`}>{inr(r.cumulative)}</p>{r.early_charges > 0 && <p className="text-white/40 text-[10px]" data-testid={`dues-early-${r.player.player_id}`}>incl. {inr(r.early_charges)} next period</p>}</div>
                {feeTypeFilter !== "all" && (
                  <div className="text-right" data-testid={`dues-type-cumulative-${r.player.player_id}`}>
                    <p className="text-white/40 text-[10px] uppercase tracking-widest">{FEE_LABELS[feeTypeFilter]}</p>
                    <p className="font-bold text-[#D4AF37]">{inr(perTypeCumulative(r, feeTypeFilter))}</p>
                  </div>
                )}
                <button onClick={() => setAddDuesFor([r.player.player_id])} className="text-white/40 hover:text-[#D4AF37] transition-colors duration-200" title="Add dues (e.g. tournament fee)" data-testid={`add-due-btn-${r.player.player_id}`}>
                  <PlusCircle size={19} />
                </button>
                {r.package?.plan_id && (
                  <button onClick={() => setPkgRow(r)} className="text-white/40 hover:text-[#D4AF37] transition-colors duration-200" title="Extend plan / discount next plan" data-testid={`pkg-change-btn-${r.player.player_id}`}>
                    <CalendarClock size={19} />
                  </button>
                )}
                {!r.paused && (
                  <button onClick={() => setAdjustRow(r)} className="text-white/40 hover:text-[#D4AF37] transition-colors duration-200" title="Adjust this month's fees" data-testid={`adjust-btn-${r.player.player_id}`}>
                    <SlidersHorizontal size={18} />
                  </button>
                )}
                {r.paused ? (
                  <span className="text-[10px] uppercase tracking-widest px-2 py-1 border border-blue-400/40 text-blue-300" data-testid={`paused-badge-${r.player.player_id}`}>On Hold</span>
                ) : (
                  <button onClick={() => setPauseFor(r.player.player_id)} className="text-white/40 hover:text-[#D4AF37] transition-colors duration-200" title="Pause billing this month" data-testid={`pause-btn-${r.player.player_id}`}>
                    <PauseCircle size={20} />
                  </button>
                )}
              </div>
            </div>
            {!r.paused && r.total_charges > 0 && (
              <p className="text-white/40 text-xs mt-3">
                {["group", "transport", "pt", "previous_dues", "registration", "extra"].filter((t) => r.charges[t] > 0).map((t) => `${FEE_LABELS[t]} ${inr(r.charges[t])}`).join(" · ")}
                {r.override && <span className="text-[#D4AF37] ml-2" data-testid={`override-badge-${r.player.player_id}`}>· Adjusted{r.override.reason ? ` (${r.override.reason})` : ""}</span>}
              </p>
            )}
            {r.paused && <p className="text-blue-300/70 text-xs mt-3">Reason: {r.pause_reason}</p>}
            {(r.extras || []).length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3" data-testid={`dues-extras-${r.player.player_id}`}>
                {r.extras.map((x) => (
                  <span key={x.id} className="flex items-center gap-2 text-[11px] border border-[#D4AF37]/30 bg-[#D4AF37]/5 text-[#D4AF37] px-2 py-1">
                    {x.label} {inr(x.amount)} · {fmtIso(x.date)}
                    <button onClick={() => removeExtra(x)} className="text-red-400/80 hover:text-red-300" data-testid={`extra-remove-${x.id}`}><X size={12} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
        {filtered.length === 0 && <p className="text-white/40 text-sm border border-white/10 p-8 text-center">No dues match the current filters for {monthLabel(month)}.</p>}
      </div>

      {pauseFor && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4" onClick={() => setPauseFor(null)} data-testid="pause-modal">
          <form onSubmit={addPause} onClick={(e) => e.stopPropagation()} className="bg-[#0A0A0A] border border-white/10 p-8 w-full max-w-md">
            <div className="flex justify-between items-start mb-6">
              <h2 className="font-heading text-xl font-black">Hold Billing — {monthLabel(month)}</h2>
              <button type="button" onClick={() => setPauseFor(null)} className="text-white/50 hover:text-white"><X size={20} /></button>
            </div>
            <p className="text-white/50 text-sm mb-5">{pauseFor} will not be billed for {monthLabel(month)}. This month stays unbilled even after reactivation.</p>
            <input required placeholder="Reason (e.g. injury break)" value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls + " w-full mb-4"} data-testid="pause-reason" />
            <button type="submit" className="w-full bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-xs px-6 py-3 rounded-sm hover:bg-[#F3D059] transition-colors duration-200" data-testid="pause-confirm">Confirm Hold</button>
          </form>
        </div>
      )}

      {addDuesFor && <AddDuesModal preselect={addDuesFor} onClose={() => setAddDuesFor(null)} onSaved={load} />}
      {pkgRow && <PackageChangeModal row={pkgRow} onClose={() => setPkgRow(null)} onSaved={load} />}
      {adjustRow && <OverrideModal row={adjustRow} month={month} onClose={() => setAdjustRow(null)} onSaved={load} />}
    </div>
  );
}
